'use client';

import React from 'react';
import Link from 'next/link';

interface HeroSectionProps {
    isVisible: boolean;
}

export default function HeroSection({ isVisible }: HeroSectionProps) {
    return (
        <section className="relative pt-28 pb-16 sm:pt-32 sm:pb-20 lg:pt-40 lg:pb-28">
            {/* Subtle background */}
            <div className="absolute inset-0 bg-gradient-to-b from-violet-50/50 to-white" />

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
                <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-start">
                    {/* Left: Content */}
                    <div className={`transition-all duration-700 flex flex-col items-center lg:items-start ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
                        {/* Product Hunt Embed */}
                        {/* <div className="mb-8 flex flex-col items-center lg:items-start gap-3">
                            <span className="text-sm font-medium text-gray-500 uppercase tracking-wide">Support our launch on Jan 28th</span>
                            <a href="https://www.producthunt.com/products/waitlistly-2?embed=true&utm_source=badge-featured&utm_medium=badge&utm_campaign=badge-waitlistly-2" target="_blank" rel="noopener noreferrer">
                                <img
                                    src="https://api.producthunt.com/widgets/embed-image/v1/featured.svg?post_id=1067157&theme=dark&t=1769359179366"
                                    alt="Waitlistly - See if people want your idea before you build it | Product Hunt"
                                    width="180"
                                    height="40"
                                />
                            </a>
                        </div> */}

                        <h1 className="text-5xl sm:text-6xl md:text-6xl lg:text-7xl leading-[1.1] tracking-tight text-gray-900 text-center lg:text-left">
                            <span className="font-medium">Know if your</span><br />
                            <span className="font-extrabold">idea has demand</span>
                        </h1>

                        <div className="w-full flex justify-center lg:justify-start">
                            <ul className="mt-8 space-y-4 flex flex-col items-start text-left">
                                <li className="flex items-start gap-2 sm:gap-3">
                                    <div className="w-7 h-7 sm:w-8 sm:h-8 bg-violet-100 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                                        <svg className="w-4 h-4 text-violet-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                        </svg>
                                    </div>
                                    <span className="text-base sm:text-lg text-gray-600">Build a custom waitlist page <span className="text-violet-600 font-medium font-bold">in &lt; 5 mins</span></span>
                                </li>
                                <li className="flex items-start gap-2 sm:gap-3">
                                    <div className="w-7 h-7 sm:w-8 sm:h-8 bg-violet-100 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                                        <svg className="w-4 h-4 text-violet-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09z" />
                                        </svg>
                                    </div>
                                    <span className="text-base sm:text-lg text-gray-600">AI builds <span className="text-violet-600 font-medium font-bold">layout, styles & writes copy</span></span>
                                </li>
                                <li className="flex items-start gap-2 sm:gap-3">
                                    <div className="w-7 h-7 sm:w-8 sm:h-8 bg-violet-100 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                                        <svg className="w-4 h-4 text-violet-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                                        </svg>
                                    </div>
                                    <span className="text-base sm:text-lg text-gray-600">Collect <span className="text-violet-600 font-medium font-bold">real signups</span> from real people</span>
                                </li>
                                <li className="flex items-start gap-2 sm:gap-3">
                                    <div className="w-7 h-7 sm:w-8 sm:h-8 bg-violet-100 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                                        <svg className="w-4 h-4 text-violet-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                                        </svg>
                                    </div>
                                    <span className="text-base sm:text-lg text-gray-600">Decide if the idea is <span className="text-violet-600 font-medium font-bold">worth pursuing</span></span>
                                </li>
                            </ul>
                        </div>

                        <div className="mt-10 flex flex-col items-center lg:items-start w-full">
                            <Link
                                href="/signup"
                                className="group w-full lg:max-w-md inline-flex items-center justify-center gap-2 sm:gap-3 px-6 sm:px-8 py-3.5 sm:py-4 bg-violet-600 text-white font-semibold text-base sm:text-lg rounded-md hover:bg-violet-700 transition-all shadow-lg shadow-violet-600/20 hover:shadow-xl hover:shadow-violet-600/30 hover:-translate-y-0.5"
                            >
                                Start Building Free
                            </Link>
                        </div>

                        <div className="mt-8 flex items-center justify-center lg:justify-start gap-6 lg:gap-8 w-full">
                            <div className="flex items-center gap-3">
                                <div className="flex -space-x-3">
                                    {[
                                        'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?auto=format&fit=crop&w=64&h=64', // Business woman
                                        'https://images.unsplash.com/photo-1566492031773-4f4e44671857?auto=format&fit=crop&w=64&h=64', // Man smiling
                                        'https://images.unsplash.com/photo-1595152772835-219674b2a8a6?auto=format&fit=crop&w=64&h=64', // Man smiling
                                        'https://images.unsplash.com/photo-1600486913747-55e5470d6f40?auto=format&fit=crop&w=64&h=64', // Telugu guy

                                    ].map((src, i) => (
                                        <div key={i} className="w-10 h-10 rounded-full border-2 border-white ring-1 ring-gray-100 overflow-hidden relative">
                                            {/* eslint-disable-next-line @next/next/no-img-element */}
                                            <img
                                                src={src}
                                                alt={`Founder ${i + 1}`}
                                                className="w-full h-full object-cover"
                                            />
                                        </div>
                                    ))}
                                </div>
                                <div className="flex flex-col text-left">
                                    <div className="flex items-center gap-1">
                                        <div className="flex gap-0.5">
                                            {[1, 2, 3, 4, 5].map((star) => (
                                                <svg key={star} className="w-3.5 h-3.5 text-amber-400 fill-amber-400" viewBox="0 0 20 20">
                                                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                                                </svg>
                                            ))}
                                        </div>
                                    </div>
                                    <span className="text-sm text-gray-600 font-medium">400+ founders validating</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Right: Dashboard Preview - Visible on all screens */}
                    <div className={`relative transition-all duration-700 delay-200 mt-12 lg:mt-0 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
                        <DashboardMockup />
                    </div>
                </div>
            </div>
        </section>
    );
}

function DashboardMockup() {
    return (
        <div className="relative mx-auto max-w-[360px] sm:max-w-none">
            {/* Main dashboard card */}
            <div className="bg-white rounded-2xl sm:rounded-3xl border border-gray-200 shadow-xl sm:shadow-2xl shadow-gray-200/60 overflow-hidden">
                {/* Header */}
                <div className="flex items-center justify-between px-4 sm:px-6 py-3 sm:py-4 border-b border-gray-100 bg-gray-50/50">
                    <div className="flex items-center gap-3">
                        <div className="w-8 h-8 sm:w-10 sm:h-10 bg-violet-600 rounded-lg sm:rounded-xl flex items-center justify-center">
                            <span className="text-white font-bold text-xs sm:text-sm">M</span>
                        </div>
                        <div>
                            <div className="flex items-center gap-2">
                                <span className="font-semibold text-gray-900 text-sm sm:text-base">MyStartup</span>
                                <span className="hidden sm:inline-block px-2 py-0.5 bg-emerald-100 text-emerald-700 text-xs font-semibold rounded-full">LIVE</span>
                            </div>
                            <span className="text-xs sm:text-sm text-gray-500 block truncate max-w-[120px] sm:max-w-none">mystartup.waitlistly.live</span>
                        </div>
                    </div>
                    <button className="px-3 sm:px-4 py-1.5 sm:py-2 bg-violet-600 text-white text-xs sm:text-sm font-medium rounded-lg sm:rounded-xl hover:bg-violet-700 transition-colors">
                        Visit Live
                    </button>
                </div>

                {/* Stats */}
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 sm:gap-4 p-4 sm:p-6">
                    <StatCard icon="eye" label="Total Views" value="847" color="violet" />
                    <StatCard icon="users" label="Total Leads" value="142" color="emerald" />
                    <div className="col-span-2 sm:col-span-1">
                        <StatCard icon="chart" label="Conversion" value="16.8%" color="amber" highlight />
                    </div>
                </div>

                {/* Recent signups */}
                <div className="px-4 sm:px-6 pb-4 sm:pb-6">
                    <div className="bg-gray-50 rounded-xl sm:rounded-2xl border border-gray-100 overflow-hidden">
                        <div className="flex items-center justify-between px-4 sm:px-5 py-2.5 sm:py-3 border-b border-gray-100">
                            <div className="flex items-center gap-2">
                                <span className="font-semibold text-gray-900 text-sm sm:text-base">Leads</span>
                                <span className="px-2 py-0.5 bg-violet-100 text-violet-700 text-xs font-medium rounded-full">142</span>
                            </div>
                            <button className="text-xs sm:text-sm text-violet-600 font-medium hover:text-violet-700 flex items-center gap-1">
                                <svg className="w-3.5 h-3.5 sm:w-4 sm:h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                                </svg>
                                Export
                            </button>
                        </div>
                        <div className="divide-y divide-gray-100">
                            <LeadRow email="alex@techstartup.io" time="2 min ago" />
                            <LeadRow email="sarah@company.com" time="18 min ago" />
                            <LeadRow email="mike@indie.dev" time="1 hour ago" />
                        </div>
                    </div>
                </div>
            </div>

            {/* Floating notification - Hidden on really small screens, visible on mobile */}
            <div className="hidden xs:block absolute -right-2 sm:-right-4 top-16 sm:top-20 bg-white border border-gray-200 rounded-xl sm:rounded-2xl p-3 sm:p-4 shadow-xl animate-float max-w-[160px] sm:max-w-none">
                <div className="flex items-center gap-2 sm:gap-3">
                    <div className="w-8 h-8 sm:w-10 sm:h-10 bg-emerald-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <svg className="w-4 h-4 sm:w-5 sm:h-5 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                        </svg>
                    </div>
                    <div className="min-w-0">
                        <div className="font-semibold text-gray-900 text-xs sm:text-base">New signup!</div>
                        <div className="text-xs sm:text-sm text-gray-500 truncate">alex@techstartup.io</div>
                    </div>
                </div>
            </div>

            {/* Floating stats - Hidden on really small screens */}
            <div className="hidden xs:block absolute -left-2 sm:-left-4 bottom-16 sm:bottom-20 bg-white border border-gray-200 rounded-xl sm:rounded-2xl p-3 sm:p-4 shadow-xl animate-float-delayed max-w-[160px] sm:max-w-none">
                <div className="flex items-center gap-2 sm:gap-3">
                    <div className="w-8 h-8 sm:w-10 sm:h-10 bg-violet-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <svg className="w-4 h-4 sm:w-5 sm:h-5 text-violet-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                        </svg>
                    </div>
                    <div className="min-w-0">
                        <div className="font-semibold text-gray-900 text-xs sm:text-base">16.8% conv.</div>
                        <div className="text-xs sm:text-sm text-gray-500 truncate">Strong demand</div>
                    </div>
                </div>
            </div>
        </div>
    );
}

function StatCard({ icon, label, value, color, highlight }: { icon: string; label: string; value: string; color: string; highlight?: boolean }) {
    const colors = {
        violet: { bg: 'bg-violet-100', text: 'text-violet-600' },
        emerald: { bg: 'bg-emerald-100', text: 'text-emerald-600' },
        amber: { bg: 'bg-amber-100', text: 'text-amber-600' },
    };
    const c = colors[color as keyof typeof colors];

    const icons = {
        eye: <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />,
        users: <path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />,
        chart: <path strokeLinecap="round" strokeLinejoin="round" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />,
    };

    return (
        <div className={`p-3 sm:p-4 rounded-xl sm:rounded-2xl border ${highlight ? 'bg-violet-50 border-violet-200' : 'bg-white border-gray-100'}`}>
            <div className="flex items-center justify-between mb-2 sm:mb-3">
                <span className={`text-xs sm:text-sm font-medium ${highlight ? 'text-violet-700' : 'text-gray-500'}`}>{label}</span>
                <div className={`w-6 h-6 sm:w-8 sm:h-8 ${c.bg} rounded-md sm:rounded-lg flex items-center justify-center`}>
                    <svg className={`w-3.5 h-3.5 sm:w-4 sm:h-4 ${c.text}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        {icons[icon as keyof typeof icons]}
                    </svg>
                </div>
            </div>
            <div className={`text-xl sm:text-3xl font-bold ${highlight ? 'text-violet-700' : 'text-gray-900'}`}>{value}</div>
        </div>
    );
}

function LeadRow({ email, time }: { email: string; time: string }) {
    return (
        <div className="flex items-center justify-between px-4 sm:px-5 py-2.5 sm:py-3 hover:bg-gray-50 transition-colors">
            <div className="flex items-center gap-2 sm:gap-3">
                <div className="w-7 h-7 sm:w-9 sm:h-9 bg-violet-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-violet-600 text-xs sm:text-sm font-semibold">{email[0].toUpperCase()}</span>
                </div>
                <span className="text-gray-700 font-medium text-xs sm:text-sm truncate max-w-[120px] sm:max-w-none">{email}</span>
            </div>
            <span className="text-xs sm:text-sm text-gray-500 whitespace-nowrap">{time}</span>
        </div>
    );
}
